// Integration Registry
// Manages registration and discovery of integration plugins

import {
  IntegrationType,
  IntegrationConfig,
  IntegrationPlugin,
  ValidationResult
} from '../../../types/integration';
import { BaseIntegration } from '../base/BaseIntegration';

export interface IntegrationFactory {
  create(config: IntegrationConfig): Promise<BaseIntegration>;
  validate(config: Partial<IntegrationConfig>): Promise<ValidationResult>;
  getConfigSchema(): Record<string, any>;
  getSupportedFeatures(): string[];
}

export interface RegisteredIntegration {
  id: string;
  name: string;
  type: IntegrationType;
  platform: string;
  version: string;
  factory: IntegrationFactory;
  plugin?: IntegrationPlugin;
  capabilities: string[];
  configSchema: Record<string, any>;
  registeredAt: Date;
  enabled: boolean;
}

export class IntegrationRegistry {
  private static instance: IntegrationRegistry;
  private registrations: Map<string, RegisteredIntegration> = new Map();
  private activeIntegrations: Map<string, BaseIntegration> = new Map();

  private constructor() {}

  static getInstance(): IntegrationRegistry {
    if (!IntegrationRegistry.instance) {
      IntegrationRegistry.instance = new IntegrationRegistry();
    }
    return IntegrationRegistry.instance;
  }

  /**
   * Register an integration type with its factory
   */
  register(
    id: string,
    name: string,
    type: IntegrationType,
    platform: string,
    version: string,
    factory: IntegrationFactory,
    plugin?: IntegrationPlugin
  ): void {
    const registration: RegisteredIntegration = {
      id,
      name,
      type,
      platform,
      version,
      factory,
      plugin,
      capabilities: factory.getSupportedFeatures(),
      configSchema: factory.getConfigSchema(),
      registeredAt: new Date(),
      enabled: true
    };

    this.registrations.set(id, registration);
    
    console.log(`Registered integration: ${name} (${type}/${platform}) v${version}`);
  }

  /**
   * Unregister an integration type
   */
  unregister(id: string): void {
    const registration = this.registrations.get(id);
    if (registration) {
      // Destroy any active integrations of this type
      this.destroyIntegrationsOfType(id);
      
      this.registrations.delete(id);
      console.log(`Unregistered integration: ${registration.name}`);
    }
  }

  /**
   * Get all registered integrations
   */
  getRegistrations(): RegisteredIntegration[] {
    return Array.from(this.registrations.values());
  }

  /**
   * Get registrations by type
   */
  getRegistrationsByType(type: IntegrationType): RegisteredIntegration[] {
    return Array.from(this.registrations.values()).filter(reg => reg.type === type);
  }

  /**
   * Get registration by platform
   */
  getRegistrationByPlatform(platform: string): RegisteredIntegration | undefined {
    return Array.from(this.registrations.values()).find(reg => reg.platform === platform);
  }

  /**
   * Get registration by ID
   */
  getRegistration(id: string): RegisteredIntegration | undefined {
    return this.registrations.get(id);
  }

  /**
   * Check if an integration type is registered
   */
  isRegistered(id: string): boolean {
    return this.registrations.has(id);
  }

  /**
   * Enable/disable an integration type
   */
  setEnabled(id: string, enabled: boolean): void {
    const registration = this.registrations.get(id);
    if (registration) {
      registration.enabled = enabled;
      
      if (!enabled) {
        // Destroy active integrations when disabled
        this.destroyIntegrationsOfType(id);
      }
    }
  }

  /**
   * Create an integration instance
   */
  async createIntegration(config: IntegrationConfig): Promise<BaseIntegration> {
    const registrationId = this.getRegistrationId(config.type, config.platform);
    const registration = this.registrations.get(registrationId);
    
    if (!registration) {
      throw new Error(`No registration found for ${config.type}/${config.platform}`);
    }

    if (!registration.enabled) {
      throw new Error(`Integration ${registrationId} is disabled`);
    }

    // Validate configuration
    const validation = await registration.factory.validate(config);
    if (!validation.valid) {
      throw new Error(`Configuration validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
    }

    // Create integration instance
    const integration = await registration.factory.create(config);
    
    // Store active integration
    this.activeIntegrations.set(config.id, integration);
    
    console.log(`Created integration instance: ${config.name} (${config.id})`);
    
    return integration;
  }

  /**
   * Get active integration instance
   */
  getIntegration(integrationId: string): BaseIntegration | undefined {
    return this.activeIntegrations.get(integrationId);
  }

  /**
   * Get all active integrations
   */
  getActiveIntegrations(): BaseIntegration[] {
    return Array.from(this.activeIntegrations.values());
  }

  /**
   * Get active integrations by type
   */
  getActiveIntegrationsByType(type: IntegrationType): BaseIntegration[] {
    return Array.from(this.activeIntegrations.values()).filter(
      integration => integration.getConfig().type === type
    );
  }

  /**
   * Destroy an integration instance
   */
  async destroyIntegration(integrationId: string): Promise<void> {
    const integration = this.activeIntegrations.get(integrationId);
    if (integration) {
      try {
        await integration.destroy();
      } catch (error) {
        console.error(`Error destroying integration ${integrationId}:`, error);
      }
      
      this.activeIntegrations.delete(integrationId);
      console.log(`Destroyed integration instance: ${integrationId}`);
    }
  }

  /**
   * Destroy all active integrations
   */
  async destroyAllIntegrations(): Promise<void> {
    const integrationIds = Array.from(this.activeIntegrations.keys());
    
    await Promise.all(
      integrationIds.map(id => this.destroyIntegration(id))
    );
  }

  /**
   * Validate integration configuration
   */
  async validateConfig(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    if (!config.type || !config.platform) {
      return {
        valid: false,
        errors: [
          { field: 'type', message: 'Integration type is required', code: 'REQUIRED' },
          { field: 'platform', message: 'Platform is required', code: 'REQUIRED' }
        ],
        warnings: []
      };
    }

    const registrationId = this.getRegistrationId(config.type, config.platform);
    const registration = this.registrations.get(registrationId);
    
    if (!registration) {
      return {
        valid: false,
        errors: [
          { 
            field: 'platform', 
            message: `No registration found for ${config.type}/${config.platform}`, 
            code: 'NOT_REGISTERED' 
          }
        ],
        warnings: []
      };
    }

    // Use the factory's validation
    return await registration.factory.validate(config);
  }

  /**
   * Get configuration schema for an integration type
   */
  getConfigSchema(type: IntegrationType, platform: string): Record<string, any> | undefined {
    const registrationId = this.getRegistrationId(type, platform);
    const registration = this.registrations.get(registrationId);
    
    return registration?.configSchema;
  }

  /**
   * Get supported features for an integration type
   */
  getSupportedFeatures(type: IntegrationType, platform: string): string[] {
    const registrationId = this.getRegistrationId(type, platform);
    const registration = this.registrations.get(registrationId);
    
    return registration?.capabilities || [];
  }

  /**
   * Check if a feature is supported
   */
  isFeatureSupported(type: IntegrationType, platform: string, feature: string): boolean {
    const features = this.getSupportedFeatures(type, platform);
    return features.includes(feature);
  }

  /**
   * Get available platforms for an integration type
   */
  getAvailablePlatforms(type: IntegrationType): string[] {
    return Array.from(this.registrations.values())
      .filter(reg => reg.type === type && reg.enabled)
      .map(reg => reg.platform);
  }

  /**
   * Get integration statistics
   */
  getStatistics(): {
    totalRegistrations: number;
    enabledRegistrations: number;
    activeIntegrations: number;
    byType: Record<IntegrationType, number>;
    byPlatform: Record<string, number>;
  } {
    const registrations = Array.from(this.registrations.values());
    const activeIntegrations = Array.from(this.activeIntegrations.values());

    const byType: Record<IntegrationType, number> = {} as any;
    const byPlatform: Record<string, number> = {};

    registrations.forEach(reg => {
      byType[reg.type] = (byType[reg.type] || 0) + 1;
      byPlatform[reg.platform] = (byPlatform[reg.platform] || 0) + 1;
    });

    return {
      totalRegistrations: registrations.length,
      enabledRegistrations: registrations.filter(reg => reg.enabled).length,
      activeIntegrations: activeIntegrations.length,
      byType,
      byPlatform
    };
  }

  /**
   * Initialize plugin from external source
   */
  async loadPlugin(pluginPath: string): Promise<void> {
    try {
      // In a real implementation, this would dynamically load a plugin
      // For now, this is a placeholder for the plugin loading mechanism
      console.log(`Loading plugin from: ${pluginPath}`);
      
      // The plugin would export a registration function
      // const plugin = await import(pluginPath);
      // await plugin.register(this);
      
    } catch (error) {
      console.error(`Failed to load plugin from ${pluginPath}:`, error);
      throw error;
    }
  }

  /**
   * Auto-discover and register built-in integrations
   */
  async discoverIntegrations(): Promise<void> {
    // This would scan for integration implementations and register them
    // For now, we'll register the base types as examples
    
    console.log('Discovering built-in integrations...');
    
    // Built-in integrations would be registered here
    // Example registrations would be added by specific integration implementations
  }

  // Private helper methods

  private getRegistrationId(type: IntegrationType, platform: string): string {
    return `${type}:${platform}`;
  }

  private async destroyIntegrationsOfType(registrationId: string): Promise<void> {
    const registration = this.registrations.get(registrationId);
    if (!registration) return;

    const integrationsToDestroy = Array.from(this.activeIntegrations.entries())
      .filter(([_, integration]) => {
        const config = integration.getConfig();
        return this.getRegistrationId(config.type, config.platform) === registrationId;
      })
      .map(([id, _]) => id);

    await Promise.all(
      integrationsToDestroy.map(id => this.destroyIntegration(id))
    );
  }
}

// Singleton instance export
export const integrationRegistry = IntegrationRegistry.getInstance();